import { useMemo } from "react";

export type Role = "ADMIN" | "OFFICE_LEAD" | "SENIOR_OFFICE" | "MANAGER" | "TECHNICIAN" | "ACCOUNTANT" | "CASHIER";

interface Permissions {
  // Меню
  canSeeDeals: boolean;
  canSeeClients: boolean;
  canSeeProducts: boolean;
  canSeeWarehouse: boolean;
  canSeeReports: boolean;
  canSeeCompanies: boolean;
  canSeeFinished: boolean;
  canSeeSiteCustomers: boolean;
  canSeeCredits: boolean;
  canSeeIntegrations: boolean;
  canSeeSettings: boolean;
  // Долги по наличным (техники сдают в кассу)
  canSeeCashDebts: boolean;
  // Отчёт «Заработок» — доступен всем сотрудникам (каждый видит своё; руководители — по роли)
  canSeeEarnings: boolean;
  // Действия
  canDeleteDeals: boolean;
  /** Старший офиса не удаляет сразу, а отправляет запрос на удаление */
  canRequestDeleteDeals: boolean;
  canDeleteProducts: boolean;
  // Закуп и финансы: только администратор и бухгалтер
  canSeeCost: boolean;
  // Отчёты
  canSeeFinanceReports: boolean;
  canSeeStaffReport: boolean;
  /** устаревшие флаги — оставлены для совместимости (= canSeeFinanceReports) */
  canSeeHalykReport: boolean;
  canSeeSalesReport: boolean;
  // Настройки — вкладки
  settingsTabs: string[]; // список разрешённых вкладок
}

const FULL_SETTINGS = ["organization", "pipeline", "users", "productCategories", "products", "clients",
  "siteSettings", "halykMarket", "kaspi", "notifications", "ukassa", "monttech", "paymentTypes", "mail", "alStyle"];

const PERMISSIONS: Record<Role, Permissions> = {
  // Владелец
  ADMIN: {
    canSeeDeals: true, canSeeClients: true, canSeeProducts: true,
    canSeeWarehouse: true, canSeeReports: true, canSeeCompanies: true,
    canSeeFinished: true, canSeeSiteCustomers: true, canSeeCredits: true,
    canSeeIntegrations: true, canSeeSettings: true,
    canDeleteDeals: true, canRequestDeleteDeals: false, canDeleteProducts: true,
    canSeeCost: true, canSeeFinanceReports: true, canSeeStaffReport: true,
    canSeeHalykReport: true, canSeeSalesReport: true,
    canSeeCashDebts: true,
    canSeeEarnings: true,
    settingsTabs: FULL_SETTINGS,
  },
  // Руководитель: всё, кроме закупа и финансовых отчётов
  OFFICE_LEAD: {
    canSeeDeals: true, canSeeClients: true, canSeeProducts: true,
    canSeeWarehouse: true, canSeeReports: true, canSeeCompanies: true,
    canSeeFinished: true, canSeeSiteCustomers: true, canSeeCredits: true,
    canSeeIntegrations: true, canSeeSettings: true,
    canDeleteDeals: true, canRequestDeleteDeals: false, canDeleteProducts: true,
    canSeeCost: false, canSeeFinanceReports: false, canSeeStaffReport: true,
    canSeeHalykReport: false, canSeeSalesReport: false,
    canSeeCashDebts: true,
    canSeeEarnings: true,
    settingsTabs: ["organization", "pipeline", "users", "productCategories", "products", "clients", "paymentTypes"],
  },
  SENIOR_OFFICE: {
    canSeeDeals: true, canSeeClients: true, canSeeProducts: true,
    canSeeWarehouse: true, canSeeReports: true, canSeeCompanies: true,
    canSeeFinished: true, canSeeSiteCustomers: true, canSeeCredits: false,
    canSeeIntegrations: false, canSeeSettings: true,
    canDeleteDeals: false, canRequestDeleteDeals: true, canDeleteProducts: false,
    canSeeCost: false, canSeeFinanceReports: false, canSeeStaffReport: true,
    canSeeHalykReport: false, canSeeSalesReport: false,
    canSeeCashDebts: true,
    canSeeEarnings: true,
    settingsTabs: ["productCategories", "products"],
  },
  MANAGER: {
    canSeeDeals: true, canSeeClients: false, canSeeProducts: true,
    canSeeWarehouse: false, canSeeReports: false, canSeeCompanies: false,
    canSeeFinished: false, canSeeSiteCustomers: false, canSeeCredits: false,
    canSeeIntegrations: false, canSeeSettings: true,
    canDeleteDeals: false, canRequestDeleteDeals: false, canDeleteProducts: false,
    canSeeCost: false, canSeeFinanceReports: false, canSeeStaffReport: false,
    canSeeHalykReport: false, canSeeSalesReport: false,
    canSeeCashDebts: false,
    canSeeEarnings: true,
    settingsTabs: ["products"],
  },
  // Техник-монтажник: свои сделки и наряды, все клиенты (принимает звонки)
  TECHNICIAN: {
    canSeeDeals: true, canSeeClients: true, canSeeProducts: true,
    canSeeWarehouse: false, canSeeReports: false, canSeeCompanies: false,
    canSeeFinished: false, canSeeSiteCustomers: false, canSeeCredits: false,
    canSeeIntegrations: false, canSeeSettings: false,
    canDeleteDeals: false, canRequestDeleteDeals: false, canDeleteProducts: false,
    canSeeCost: false, canSeeFinanceReports: false, canSeeStaffReport: false,
    canSeeHalykReport: false, canSeeSalesReport: false,
    canSeeCashDebts: true,
    canSeeEarnings: true,
    settingsTabs: [],
  },
  // Кассир: только касса (отдельный экран, меню CRM ему не показывается)
  CASHIER: {
    canSeeDeals: false, canSeeClients: false, canSeeProducts: false,
    canSeeWarehouse: false, canSeeReports: false, canSeeCompanies: false,
    canSeeFinished: false, canSeeSiteCustomers: false, canSeeCredits: false,
    canSeeIntegrations: false, canSeeSettings: false,
    canDeleteDeals: false, canRequestDeleteDeals: false, canDeleteProducts: false,
    canSeeCost: false, canSeeFinanceReports: false, canSeeStaffReport: false,
    canSeeHalykReport: false, canSeeSalesReport: false,
    canSeeCashDebts: false,
    canSeeEarnings: false,
    settingsTabs: [],
  },
  // Бухгалтер: финансы, закуп, склад, документы
  ACCOUNTANT: {
    canSeeDeals: true, canSeeClients: true, canSeeProducts: true,
    canSeeWarehouse: true, canSeeReports: true, canSeeCompanies: true,
    canSeeFinished: true, canSeeSiteCustomers: false, canSeeCredits: true,
    canSeeIntegrations: false, canSeeSettings: true,
    canDeleteDeals: false, canRequestDeleteDeals: false, canDeleteProducts: false,
    canSeeCost: true, canSeeFinanceReports: true, canSeeStaffReport: true,
    canSeeHalykReport: true, canSeeSalesReport: true,
    canSeeCashDebts: true,
    canSeeEarnings: true,
    settingsTabs: ["products", "productCategories"],
  },
};

function getRoleFromToken(): Role {
  try {
    const token = localStorage.getItem("token");
    if (!token) return "MANAGER";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return (payload.role as Role) ?? "MANAGER";
  } catch {
    return "MANAGER";
  }
}

export function usePermissions(): Permissions {
  return useMemo(() => {
    const role = getRoleFromToken();
    return PERMISSIONS[role] ?? PERMISSIONS.MANAGER;
  }, []);
}

export function getPermissions(): Permissions {
  const role = getRoleFromToken();
  return PERMISSIONS[role] ?? PERMISSIONS.MANAGER;
}

export function getRole(): Role {
  return getRoleFromToken();
}

/** id текущего сотрудника из токена */
export function getUserId(): number | null {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.sub != null ? Number(payload.sub) : null;
  } catch {
    return null;
  }
}
